/**
 * Загрузчик «после первого кадра» — отдельная точка входа сборки (`vite.config.ts`), подключается
 * prerender'ом к HTML страниц (scripts/lib/route-chunk-preloads.mjs, scripts/lib/boot-stage.mjs).
 *
 * Два режима, оба стартуют, когда кадр с оболочкой показан (запись `first-contentful-paint`):
 *
 * 1. `route-preloads` (хабы): список чанков страницы данными, загрузчик просит их `modulepreload`'ом.
 *    Зачем не `<link rel="modulepreload">` в `<head>`: на хабе их 55–65 штук, они едут вместе с entry и
 *    мешают первому кадру. Замер локальным Lighthouse (3 прогона на вариант, мобильный):
 *      без предзагрузки           /cards/credit 80, /deposits 71
 *      теги в <head> (было)       /cards/credit 72, /deposits 65  — Lighthouse считает их запросы
 *                                 частью пути к FCP: смоделированный FCP 3,2 → 4,3 с;
 *      после первого кадра (это)  /cards/credit 84, /deposits 81  — смоделированный FCP 3,0 с.
 *
 * 2. `boot-manifest` (главная): после кадра грузится само приложение — CSS, entry, vendor-чанки и чанки
 *    экрана, а следом (в простое) шрифты для следующего захода. Из `<head>` всё это убрано: симулятор
 *    Lantern берёт в граф FCP/LCP запросы, успевшие закончиться до наблюдаемого кадра, и в лаборатории PSI
 *    (всё сетевое — за ~250 мс, кадр — от 200 мс до 2,5 с) состав графа решали миллисекунды: пять
 *    запусков подряд дали 69, 82, 93, 97, 100 (scripts/lib/boot-stage.mjs).
 *
 * Не ждём кадра вечно: нет `paint` в `PerformanceObserver` — стартуем по `load`; кадра нет и спустя
 * `MAX_WAIT_MS` (фоновая вкладка, зависший рендер) — стартуем всё равно.
 *
 * Файл самодостаточный: без импортов, чтобы не тянуть за собой чанки и выполняться сразу.
 */
(() => {
  /** Дольше кадра не ждём: приложение должно загрузиться и в фоновой вкладке, и при сбое отрисовки. */
  const MAX_WAIT_MS = 4000;
  /** Шрифты — для следующего захода (`font-display: optional`), на первом просмотре они не подставляются. */
  const FONTS_IDLE_TIMEOUT_MS = 4000;

  const readJson = (id: string): unknown => {
    const element = document.getElementById(id);
    if (!element?.textContent) return null;
    try {
      return JSON.parse(element.textContent);
    } catch {
      return null;
    }
  };

  // Данные пришли из HTML, но проверка стоит дешевле, чем доверие: только собственные файлы сборки.
  const ownScript = (href: unknown): href is string =>
    typeof href === 'string' && /^\/assets\/[\w.-]+\.js$/.test(href);
  const ownStyle = (href: unknown): href is string =>
    typeof href === 'string' && /^\/assets\/[\w.-]+\.css$/.test(href);
  const ownFont = (href: unknown): href is string =>
    typeof href === 'string' && /^\/fonts\/[\w.-]+\.woff2$/.test(href);

  const addLink = (attributes: Record<string, string>): HTMLLinkElement => {
    const link = document.createElement('link');
    for (const [name, value] of Object.entries(attributes)) link.setAttribute(name, value);
    document.head.appendChild(link);
    return link;
  };
  const modulePreload = (href: string) => addLink({ rel: 'modulepreload', crossorigin: '', href });

  const legacyList = readJson('route-preloads');
  const boot = readJson('boot-manifest') as {
    css?: unknown;
    entry?: unknown;
    preload?: unknown;
    fonts?: unknown;
  } | null;

  const preloadFiles = (Array.isArray(boot?.preload) ? boot.preload : legacyList) as unknown;
  const files = Array.isArray(preloadFiles) ? preloadFiles.filter(ownScript) : [];
  const css = ownStyle(boot?.css) ? boot.css : null;
  const entry = ownScript(boot?.entry) ? boot.entry : null;
  const fonts = Array.isArray(boot?.fonts) ? boot.fonts.filter(ownFont) : [];

  if (!files.length && !css && !entry) return;

  const startFonts = () => {
    for (const href of fonts) {
      addLink({ rel: 'preload', as: 'font', type: 'font/woff2', crossorigin: '', href });
    }
  };

  let started = false;
  const start = () => {
    if (started) return;
    started = true;

    // Стили первыми и в одном запросе с кодом: main.tsx перед монтированием ждёт `link[data-app-css]`.
    if (css) {
      const link = addLink({ rel: 'stylesheet', crossorigin: '', href: css, 'data-app-css': '' });
      link.addEventListener(
        'load',
        () => {
          try {
            document.dispatchEvent(new Event('app-css-ready'));
          } catch {
            // событие нужно только ускорить main.tsx; без него он дождётся потолка
          }
        },
        { once: true }
      );
    }

    for (const href of files) modulePreload(href);

    if (entry) {
      const script = document.createElement('script');
      script.setAttribute('type', 'module');
      script.setAttribute('crossorigin', '');
      script.setAttribute('src', entry);
      document.head.appendChild(script);
    }

    if (fonts.length) {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(startFonts, { timeout: FONTS_IDLE_TIMEOUT_MS });
      } else {
        window.setTimeout(startFonts, 2000);
      }
    }
  };

  const startOnLoad = () => {
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  };

  const paintSupported =
    typeof PerformanceObserver !== 'undefined' &&
    Boolean(PerformanceObserver.supportedEntryTypes?.includes('paint'));
  if (paintSupported) {
    try {
      const observer = new PerformanceObserver((list, self) => {
        if (list.getEntriesByName('first-contentful-paint').length) {
          self.disconnect();
          start();
        }
      });
      observer.observe({ type: 'paint', buffered: true });
      // Кадр так и не показан (фоновая вкладка, сбой отрисовки): приложение всё равно должно загрузиться.
      window.setTimeout(start, MAX_WAIT_MS);
    } catch {
      startOnLoad();
    }
  } else {
    startOnLoad();
  }
})();
